type SoundFn = (ctx: AudioContext) => void;

// === WAITER CALL SOUNDS ===

const waiterBell: SoundFn = (ctx) => {
  // Classic ding-dong-ding
  [[830, 0, 0.4], [1046, 0.2, 0.3], [830, 0.45, 0.35]].forEach(([freq, delay, vol]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(vol, ctx.currentTime + delay);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.6);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + delay);
    osc.stop(ctx.currentTime + delay + 0.6);
  });
};

const waiterChime: SoundFn = (ctx) => {
  // Ascending chime
  [[523, 0], [659, 0.15], [784, 0.3], [1047, 0.45]].forEach(([freq, delay]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.5);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + delay);
    osc.stop(ctx.currentTime + delay + 0.5);
  });
};

const waiterUrgent: SoundFn = (ctx) => {
  // Rapid double beep x2
  [[880, 0], [880, 0.15], [1100, 0.4], [1100, 0.55]].forEach(([freq, delay]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.2, ctx.currentTime + delay);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.1);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + delay);
    osc.stop(ctx.currentTime + delay + 0.1);
  });
};

const waiterSoft: SoundFn = (ctx) => {
  // Soft marimba-like
  [[660, 0], [880, 0.25]].forEach(([freq, delay]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.4, ctx.currentTime + delay);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.8);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + delay);
    osc.stop(ctx.currentTime + delay + 0.8);
  });
};

// === ORDER SOUNDS ===

const orderKaching: SoundFn = (ctx) => {
  // Cash register cha-ching
  [[1200, 0, "sine"], [1500, 0.08, "sine"], [2000, 0.16, "sine"]].forEach(([freq, delay, type]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type as OscillatorType;
    osc.frequency.value = freq as number;
    gain.gain.setValueAtTime(0.25, ctx.currentTime + (delay as number));
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + (delay as number) + 0.3);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + (delay as number));
    osc.stop(ctx.currentTime + (delay as number) + 0.3);
  });
};

const orderMelody: SoundFn = (ctx) => {
  // Happy ascending melody
  [[523, 0], [659, 0.12], [784, 0.24], [1047, 0.36]].forEach(([freq, delay]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.35, ctx.currentTime + delay);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + 0.4);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + delay);
    osc.stop(ctx.currentTime + delay + 0.4);
  });
};

const orderPing: SoundFn = (ctx) => {
  // Simple clean ping
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = 1000;
  gain.gain.setValueAtTime(0.4, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.8);
};

const orderFanfare: SoundFn = (ctx) => {
  // Short fanfare
  [[392, 0], [523, 0.15], [659, 0.3], [784, 0.45], [784, 0.6]].forEach(([freq, delay]) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    const dur = delay === 0.6 ? 0.6 : 0.3;
    gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + delay + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + delay);
    osc.stop(ctx.currentTime + delay + dur);
  });
};

// === EXPORTS ===

export const WAITER_SOUNDS = {
  bell: { fn: waiterBell, labelKey: "sound_bell" },
  chime: { fn: waiterChime, labelKey: "sound_chime" },
  urgent: { fn: waiterUrgent, labelKey: "sound_urgent" },
  soft: { fn: waiterSoft, labelKey: "sound_soft" },
} as const;

export const ORDER_SOUNDS = {
  kaching: { fn: orderKaching, labelKey: "sound_kaching" },
  melody: { fn: orderMelody, labelKey: "sound_melody" },
  ping: { fn: orderPing, labelKey: "sound_ping" },
  fanfare: { fn: orderFanfare, labelKey: "sound_fanfare" },
} as const;

export type WaiterSoundKey = keyof typeof WAITER_SOUNDS;
export type OrderSoundKey = keyof typeof ORDER_SOUNDS;

let sharedCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!sharedCtx || sharedCtx.state === "closed") {
    sharedCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  }
  return sharedCtx;
}

// Call this on any user interaction to unlock audio
export function unlockAudio() {
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      ctx.resume();
    }
  } catch {
    // ignore
  }
}

export function playSound(soundMap: Record<string, { fn: SoundFn }>, key: string) {
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      ctx.resume().then(() => {
        const sound = soundMap[key];
        if (sound) sound.fn(ctx);
      });
    } else {
      const sound = soundMap[key];
      if (sound) sound.fn(ctx);
    }
  } catch {
    // Audio not supported
  }
}
